import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Description } from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { Modal } from '@/components/modals/new/modal';
import { Button } from '@/components/ui/button';
import { ShareLinkForm } from './share-link-form';
import type {
  DigiTwinShareMode,
  DigiTwinShareSubmitMeta,
  DigiTwinShareValues,
} from './shared-link-settings';

export function ShareDigiTwinPopup({
  children,
  mode,
  open = false,
  defaultValues,
  onOpen,
  onShare,
  onCancel,
  pending,
  initialName,
}: React.PropsWithChildren<{
  mode: DigiTwinShareMode;
  onShare: (values: DigiTwinShareValues, meta: DigiTwinShareSubmitMeta) => void;
  onCancel: () => void;
  onOpen: () => void;
  open?: boolean;
  defaultValues?: DigiTwinShareValues;
  pending?: boolean;
  initialName?: string;
}>) {
  const { t } = useTranslation();
  const title = t(mode === 'edit' ? 'adjustSharedLink' : 'createShareLink');

  return (
    <>
      {children}
      <Modal
        isOpen={open}
        open={onOpen}
        dismiss={onCancel}
        dismissDisabled={pending}
        title={title}
        type="custom"
        overlayWrapperClassname="bg-black/50 p-4"
        className="max-h-[90vh] max-w-md overflow-y-auto rounded-lg p-6 shadow-xl"
      >
        <p aria-hidden className="pr-8 text-lg font-semibold text-text-primary">
          {title}
        </p>
        <Description className="mt-1 text-sm text-text-secondary">
          {mode === 'edit'
            ? t('adjustSharedLinkDescription', {
                name: defaultValues?.name || t('unnamedSharedLink'),
              })
            : t('createShareLinkDescription')}
        </Description>
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-3 top-3 h-7 w-7 !p-1"
          disabled={pending}
          aria-label={t('close')}
          onClick={onCancel}
        >
          <XIcon />
        </Button>
        <ShareLinkForm
          mode={mode}
          initialName={initialName}
          defaultValues={defaultValues}
          onShare={onShare}
          onCancel={onCancel}
          pending={pending}
        />
      </Modal>
    </>
  );
}
